import { useContext, useEffect, useState } from 'react'
import { Table, Button, Spacer, Flex, SearchBar } from '@jisr-hr/design-system'
import { Typography } from '@jisr-hr/ds'
import { ReactComponent as PlusIcon } from '@jisr-hr/design-system/dist/assets/icons/plus-icon.svg'
import JisrLoader from 'components/global/JisrLoader'
import { useDispatch, useSelector } from 'utils/hooks'
import StepTemplate from 'containers/public/Onboarding/components/StepTemplate'
import I18n from 'translations/i18n'
import { labels } from 'utils/whitelabel'
import { TablePagination } from 'components/global/atoms'
import { StepsTypes, TParams } from 'containers/public/Onboarding/types/onboardingTypes'
import ConfirmModal from 'containers/authorised/setting/v2/components/ConfirmModal'
import { employeesInviteAllLoad } from 'redux/organizationOnborading/invite/actionCreators'
import { useUsersnapApi } from 'components/UserSnap/UsersnapContext'
import {
  onboardingValidateAdminsInvitationLoad,
  resetStates,
} from 'redux/leavesOnboarding/validationAdminInvited/actionCreators'
import { OnboardingContext } from 'containers/public/Onboarding/context'
import { onboardingEmployeesWithEmailsLoad } from 'redux/leavesOnboarding/inviteEmployees/actionCreators'
import { useTrackingSteps } from 'containers/public/Onboarding/helper'

import Row from './Row'
import AddUsers from './AddUsers'
import CostumeAlert from './CostumeAlert'
import Styles from './style.module.css'

const InviteAdminUsers = ({ goPreviousStep, previousStep }: StepsTypes): JSX.Element => {
  const { setShowActivateAccount } = useContext(OnboardingContext)
  const dispatch = useDispatch()
  const usersnapApi = useUsersnapApi()
  const { errorMsg, fetching: fetchingValidate } = useSelector(
    ({ validationAdminInvited }) => validationAdminInvited,
  )
  const { employees, pagination, fetching } = useSelector(({ inviteEmployees }) => inviteEmployees)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [params, setParams] = useState<TParams>({
    page: 1,
    rpp: 20,
    search: '',
  })
  const [isOpen, setIsOpen] = useState(false)
  const is_all_invited = employees.every((emp) => emp.is_invited)

  const validationInviting = (): void => {
    dispatch(onboardingValidateAdminsInvitationLoad({}, true)).then(() => {
      setShowActivateAccount(true)
    })
  }

  const handleClose = (): void => {
    setIsOpen(false)
    dispatch(resetStates())
  }

  const empLoad = (): void => {
    dispatch(onboardingEmployeesWithEmailsLoad(params))
  }

  const invite_all = (): void => {
    dispatch(employeesInviteAllLoad()).then(() => {
      empLoad()
    })
  }

  const handleFiltering = (key: string, val: number): void => {
    setParams((prevState) => ({ ...prevState, [key]: val }))
  }

  const handleNext = (): void => {
    setIsOpen(true)
  }

  useEffect(() => {
    empLoad()
  }, [params])

  useEffect(() => {
    if (usersnapApi) {
      // @ts-expect-error Need to useUsersnapApi.js to TypeScript
      usersnapApi?.logEvent('Onboarding_CSAT_Last')
    }
  }, [usersnapApi])

  useTrackingSteps({ step_name: 'invite_employees', level_name: 'invite_admin_users' })

  return (
    <StepTemplate
      goNextStep={handleNext}
      goPreviousStep={goPreviousStep}
      nextStep={{ title: I18n.t('login') }}
      previousStep={previousStep}
      fetching={fetching}
    >
      {fetching && <JisrLoader absolute />}
      <Typography
        variant="heading"
        text={I18n.t('admin_users')}
      />

      <Spacer height={8} />
      <Typography
        style={{ paddingInlineEnd: '70px', color: 'var(--color-base-colors-black-500)' }}
        variant="subtitle-1"
        text={I18n.t('admin_users_desc', { app_name: labels.translation_name })}
      />

      <Spacer height={16} />
      <Flex justifyBetween>
        <SearchBar
          placeholder={I18n.t('search_employees_by_id_or_name')}
          style={{ width: 340 }}
          onChange={(_: string, search: string): void =>
            setParams((prevState) => ({
              ...prevState,
              search: search.trim(),
            }))
          }
          value={params.search}
        />
        <Flex style={{ gap: 16 }}>
          <Button
            label={I18n.t(!is_all_invited ? 'invite_all' : 'invited')}
            variant="tertiary"
            size="medium"
            disabled={is_all_invited}
            onClick={invite_all}
          />
          <Button
            label={I18n.t('add_admins_onb')}
            size="medium"
            onClick={(): void => {
              setIsFormOpen(true)
            }}
            startIcon={<PlusIcon className={Styles.plusIcon} />}
          />
        </Flex>
      </Flex>
      <AddUsers
        // onAddUser={(): void => { empLoad(); }}
        open={isFormOpen}
        onClose={(): void => {
          setIsFormOpen(false)
          empLoad()
        }}
        // onSubmit={() => { }}
      />

      <Spacer height="24px" />
      <Table
        variant="rounded"
        containerStyles={{
          maxHeight: 445,
        }}
        headers={[
          { key: 'emp_id', label: I18n.t('emp_id') },
          { key: 'email', label: I18n.t('email') },
          { key: 'action', label: I18n.t('action'), width: 150 },
        ]}
      >
        {employees.length === 0 ? (
          <Table.Row>
            <Table.Cell
              className={Styles.noData}
              colSpan={4}
            >
              {I18n.t('no_data')}
            </Table.Cell>
          </Table.Row>
        ) : (
          employees
            .filter((emp) => emp.email)
            .map((item) => (
              <Row
                key={item.id}
                row={item}
                reload={(): void => {
                  empLoad()
                }}
              />
            ))
        )}
      </Table>
      {!!employees.length && (
        <>
          <Spacer height="16px" />
          <TablePagination
            currentPage={params.page}
            totalEntries={pagination.total_entries}
            rowsPerPage={params.rpp}
            handleChange={(val: number): void => handleFiltering('page', val)}
            handleChangeRPP={(val: number): void =>
              setParams((prevState) => ({ ...prevState, rpp: val, page: 1 }))
            }
          />
        </>
      )}
      <ConfirmModal
        isOpen={isOpen}
        variant="info"
        onCancel={handleClose}
        title="confirm_title"
        message={I18n.t('confirm_msg')}
        customContent={errorMsg && <CostumeAlert />}
        onConfirm={validationInviting}
        labelConfirm="confirm_but"
        labelCancel="confirm_cancel"
        disableSubmit={!!errorMsg || fetchingValidate}
      />
    </StepTemplate>
  )
}

export default InviteAdminUsers
